import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	fieldErrorsFromChangePasswordError,
	useChangePassword,
} from "@/hooks/mutations/use-change-password";
import {
	type ChangePasswordFormValues,
	changePasswordSchema,
} from "@/schemas/user";
import { closeModal } from "@/store/modal.store";

export function ChangePasswordModal() {
	const { mutate, isPending } = useChangePassword();
	const form = useForm<ChangePasswordFormValues>({
		resolver: zodResolver(changePasswordSchema),
		defaultValues: {
			oldPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	});

	function onSubmit(data: ChangePasswordFormValues) {
		mutate(
			{
				old_password: data.oldPassword,
				new_password1: data.newPassword,
				new_password2: data.confirmPassword,
			},
			{
				onSuccess: () => {
					closeModal();
				},
				onError: (error) => {
					const fields = fieldErrorsFromChangePasswordError(error);
					if (fields.oldPassword) {
						form.setError("oldPassword", { message: fields.oldPassword });
					}
					if (fields.newPassword) {
						form.setError("newPassword", { message: fields.newPassword });
					}
					if (fields.confirmPassword) {
						form.setError("confirmPassword", {
							message: fields.confirmPassword,
						});
					}
				},
			},
		);
	}

	return (
		<DialogContent className="max-w-md rounded-md p-8">
			<DialogHeader className="space-y-2">
				<DialogTitle className="text-2xl font-bold text-slate-900">
					Alterar senha
				</DialogTitle>
				<DialogDescription className="text-slate-500 text-base">
					Informe a senha atual e escolha uma nova senha para a sua conta.
				</DialogDescription>
			</DialogHeader>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="oldPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Senha atual</FormLabel>
								<FormControl>
									<Input
										type="password"
										autoComplete="current-password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="newPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Nova senha</FormLabel>
								<FormControl>
									<Input
										type="password"
										autoComplete="new-password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="confirmPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Confirmar nova senha</FormLabel>
								<FormControl>
									<Input
										type="password"
										autoComplete="new-password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 mt-4">
						<Button
							type="button"
							variant="ghost"
							onClick={closeModal}
							disabled={isPending}
							className="flex-1 h-11 rounded-md font-bold"
						>
							Cancelar
						</Button>
						<Button
							type="submit"
							disabled={isPending}
							className="flex-1 h-11 rounded-md font-bold"
						>
							{isPending ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Salvando...
								</>
							) : (
								"Salvar senha"
							)}
						</Button>
					</DialogFooter>
				</form>
			</Form>
		</DialogContent>
	);
}
